import debounce from 'lodash.debounce'
import {
  forwardRef,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react'
import {createPortal} from 'react-dom'
import {SelectInstance} from 'react-select'
import {Search, SearchProps} from './Search'
import {ms100} from 'definitions/constant'
import {SearchUsersGroupsItem, UsersGroupsOption} from 'interfaces'

interface SearchPortalProps extends SearchProps {
  parentRef: RefObject<HTMLElement>
  menuAnchorRef?: RefObject<HTMLElement>
  ariaLabel?: string
}

interface SearchMenuPosition {
  left: number
  top: number
  width: number
}

// This search portal component is useful when parent height is less than search dropdown.
// Basically we are rendering this child dropdown component outside the DOM hierarchy of the parent component
export const SearchPortal = forwardRef<
  SelectInstance<UsersGroupsOption, false, SearchUsersGroupsItem>,
  SearchPortalProps
>(
  (
    {
      callbackFunc,
      classes,
      isNoPadding,
      parentRef,
      menuAnchorRef,
      placeholder,
      isSelectedCategoryCustomized,
      ariaLabel = 'search',
    },
    ref,
  ) => {
    // div element will be created only once
    const [searchContainerEl] = useState(document.createElement('div'))
    const [menuPosition, setMenuPosition] = useState<SearchMenuPosition>()
    const parentRect = parentRef?.current?.getBoundingClientRect()
    const menuAnchor = menuAnchorRef ?? parentRef

    const updateMenuPosition = useCallback(() => {
      const menuAnchorRect = menuAnchor.current?.getBoundingClientRect()
      // Cypress (and nested overflow) can fire scroll while the anchor is
      // temporarily unmeasured. Clearing menuPosition unmounts the react-select
      // menu portal (`menuPortalTarget={menuPosition ? document.body : undefined}`).
      if (!menuAnchorRect?.width) {
        return
      }
      const nextPosition = {
        left: menuAnchorRect.left,
        top: menuAnchorRect.bottom + 2,
        width: menuAnchorRect.width,
      }
      setMenuPosition(prev =>
        prev &&
        prev.left === nextPosition.left &&
        prev.top === nextPosition.top &&
        prev.width === nextPosition.width
          ? prev
          : nextPosition,
      )
    }, [menuAnchor])

    useLayoutEffect(() => {
      const aParentRect = parentRef?.current?.getBoundingClientRect()
      searchContainerEl.id = 'user-group-search-container'
      searchContainerEl.style.position = 'absolute'
      searchContainerEl.style.overflow = 'visible'
      searchContainerEl.style.zIndex = '20'
      searchContainerEl.style.top = `${aParentRect?.top}px`
      searchContainerEl.style.left = `${aParentRect?.left}px`
      searchContainerEl.style.width = `${aParentRect?.width}px`
      updateMenuPosition()
    }, [parentRef, searchContainerEl, updateMenuPosition])

    // this use effect is need to reposition the input element.
    // use case - delete all users of custom policy
    useEffect(() => {
      setSearchContainerEl(parentRect)
      updateMenuPosition()
    }, [parentRect?.left])

    const setSearchContainerEl = useCallback(
      debounce((parentRectInput: DOMRect | undefined) => {
        if (!parentRectInput?.width) {
          return
        }
        searchContainerEl.style.top = `${parentRectInput.top}px`
        searchContainerEl.style.left = `${parentRectInput.left}px`
        searchContainerEl.style.width = `${parentRectInput.width}px`
      }, ms100),
      [searchContainerEl],
    )

    useEffect(() => {
      const reposition = (event?: Event) => {
        if (
          event?.target instanceof Node &&
          searchContainerEl.contains(event.target)
        ) {
          return
        }

        const nextRect = parentRef.current?.getBoundingClientRect()
        if (!nextRect?.width) {
          return
        }

        setSearchContainerEl(nextRect)
        updateMenuPosition()
      }

      window.addEventListener('scroll', reposition, true)
      window.addEventListener('resize', reposition)

      return () => {
        window.removeEventListener('scroll', reposition, true)
        window.removeEventListener('resize', reposition)
        setSearchContainerEl.cancel()
      }
    }, [parentRef, searchContainerEl, setSearchContainerEl, updateMenuPosition])

    useEffect(() => {
      const bodyElement = document.getElementsByTagName('body')[0]
      bodyElement.appendChild(searchContainerEl)

      return () => {
        bodyElement.removeChild(searchContainerEl)
      }
    }, [searchContainerEl])

    return createPortal(
      <Search
        ref={ref}
        callbackFunc={callbackFunc}
        classes={classes}
        isNoPadding={isNoPadding}
        placeholder={placeholder}
        menuPosition={menuPosition}
        isSelectedCategoryCustomized={isSelectedCategoryCustomized}
        ariaLabel={ariaLabel}
      />,
      searchContainerEl,
    )
  },
)

SearchPortal.displayName = 'SearchPortal'
